import Modal from "../../components/Modal/Modal";
import { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import QRCode from "react-qr-code";
import { useToast } from "../../design-system/contexts/ToastContext";
import { navigateShareOption } from "../../common/functions";
import "./ShareSponsorCodeModal.css";

const ShareSponsorCodeModal = () => {
  const [sponsorModal, setSponsorModal] = useState(true);
  const [sponsorUrl, setSponsorUrl] = useState("");
  const [qrDetails, setQrDetails] = useState("");

  const navigate = useNavigate();
  const parentContext = useOutletContext();
  const { success } = useToast();

  const fetch_url = window.location.href.toLowerCase();
  const sponsor_code = fetch_url.includes("/events/sharesponsorcode")
    ? parentContext?.eventSponsorCode
    : localStorage.getItem("sponsorCode");

  const closeModal = () => {
    setSponsorModal(false);
    navigate(-1);
  };

  const copySponsorCode = () => {
    navigator.clipboard.writeText(sponsor_code);
    success("Sponsor code copied!");
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(sponsorUrl);
    success("URL copied!");
  };

  // Detect if device is mobile/iOS
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const shareEmail = () => {
    const subject = encodeURIComponent("Join our event");
    const body = encodeURIComponent(
      `Join our event using this sponsor link: ${sponsorUrl}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(
      `Join our event using this sponsor link: ${sponsorUrl}`
    );
    // On mobile, use direct navigation to avoid new tab issues on iOS
    if (isMobile) {
      window.location.href = `https://wa.me/?text=${text}`;
    } else {
      window.open(`https://wa.me/?text=${text}`, "_blank");
    }
  };

  const shareTwitter = () => {
    const text = encodeURIComponent(
      `Join our event using this sponsor link: ${sponsorUrl}`
    );
    if (isMobile) {
      window.location.href = `https://twitter.com/intent/tweet?text=${text}`;
    } else {
      window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank");
    }
  };

  const shareFacebook = () => {
    const url = encodeURIComponent(sponsorUrl);
    if (isMobile) {
      window.location.href = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    } else {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank");
    }
  };

  const downloadQRCode = () => {
    const qrElement = document.querySelector('.share-modal__qr-container svg');
    if (qrElement) {
      const svgData = new XMLSerializer().serializeToString(qrElement);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const pngFile = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.download = `QR-Code-${sponsor_code}.png`;
        downloadLink.href = pngFile;
        downloadLink.click();
      };
      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    }
  };

  useEffect(() => {
    const url = navigateShareOption(fetch_url, sponsor_code);
    setSponsorUrl(url);
    setQrDetails(url);
  }, [fetch_url, sponsor_code]);

  return (
    <>
      {qrDetails && (
        <Modal
          showModal={sponsorModal}
          setModal={setSponsorModal}
          onCloseModal={closeModal}
          faddedBg={true}
        >
          <div className="share-modal">
            {/* Desktop Close Button */}
            <button
              className="share-modal__close-btn"
              onClick={closeModal}
              aria-label="Close"
            >
              <Icon icon="iconamoon:close" width="20" height="20" />
            </button>

            <p className="share-modal__title">
              Share this unique QR code/sponsor code with your other team members/booth staff
              to let them access the event from their account.
            </p>

            <div className="share-modal__content">
              {/* Left Section - QR Code & Sponsor Code (mobile) / QR Code & Download (desktop) */}
              <div className="share-modal__left">
                <div className="share-modal__qr-container">
                  <QRCode
                    value={qrDetails}
                    fgColor="#000000"
                    style={{ width: "100%", height: "auto" }}
                  />
                </div>

                {/* Sponsor Code Row - Mobile Only */}
                <div className="share-modal__sponsor-row share-modal__sponsor-row--mobile">
                  <div className="share-modal__sponsor-code-box">
                    <span className="share-modal__sponsor-code-text">
                      Sponsor Code: {sponsor_code}
                    </span>
                    <button
                      className="share-modal__copy-icon-btn"
                      onClick={copySponsorCode}
                      aria-label="Copy sponsor code"
                    >
                      <Icon icon="akar-icons:copy" width="20" height="20" />
                    </button>
                  </div>
                  <button
                    className="share-modal__download-btn"
                    onClick={downloadQRCode}
                    aria-label="Download QR code"
                  >
                    <Icon icon="tdesign:download" width="20" height="20" />
                  </button>
                </div>

                {/* Download Button - Desktop Only */}
                <button
                  className="share-modal__download-btn share-modal__download-btn--desktop"
                  onClick={downloadQRCode}
                  aria-label="Download QR code"
                >
                  <Icon icon="tdesign:download" width="20" height="20" />
                  <span>Download</span>
                </button>
              </div>

              {/* Right Section - Share Options */}
              <div className="share-modal__right">
                {/* Sponsor Code - Desktop Only */}
                <div className="share-modal__info-row share-modal__info-row--desktop-only">
                  <span className="share-modal__label">Sponsor Code</span>
                  <div className="share-modal__sponsor-code-box">
                    <span className="share-modal__sponsor-code-text">
                      {sponsor_code}
                    </span>
                    <button
                      className="share-modal__copy-icon-btn"
                      onClick={copySponsorCode}
                      aria-label="Copy sponsor code"
                    >
                      <Icon icon="akar-icons:copy" width="20" height="20" />
                    </button>
                  </div>
                </div>

                {/* Share URL */}
                <div className="share-modal__info-row">
                  <span className="share-modal__label">Share URL</span>
                  <div className="share-modal__share-icons">
                    <button
                      className="share-modal__share-btn share-modal__share-btn--email"
                      onClick={shareEmail}
                      aria-label="Share via Email"
                    >
                      <Icon icon="ic:round-email" width="20" height="20" />
                    </button>
                    <button
                      className="share-modal__share-btn share-modal__share-btn--whatsapp"
                      onClick={shareWhatsApp}
                      aria-label="Share on WhatsApp"
                    >
                      <Icon icon="ic:baseline-whatsapp" width="20" height="20" />
                    </button>
                    <button
                      className="share-modal__share-btn share-modal__share-btn--twitter"
                      onClick={shareTwitter}
                      aria-label="Share on Twitter"
                    >
                      <Icon icon="ri:twitter-x-fill" width="18" height="18" />
                    </button>
                    <button
                      className="share-modal__share-btn share-modal__share-btn--facebook"
                      onClick={shareFacebook}
                      aria-label="Share on Facebook"
                    >
                      <Icon icon="ic:baseline-facebook" width="20" height="20" />
                    </button>
                  </div>
                </div>

                {/* Copy Link */}
                <div className="share-modal__info-row">
                  <span className="share-modal__label">Copy Link</span>
                  <div className="share-modal__url-container">
                    <input
                      type="text"
                      className="share-modal__url-input"
                      value={sponsorUrl}
                      readOnly
                    />
                    <button
                      className="share-modal__copy-icon-btn"
                      onClick={copyUrl}
                      aria-label="Copy link"
                    >
                      <Icon icon="akar-icons:copy" width="20" height="20" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ShareSponsorCodeModal;
